import { InjectionKey, Ref, VNode, computed, inject, provide, ref } from 'vue'

import { getCurrentInstance } from '../utils/current-instance'
import { mergeDeep } from '../utils/helpers'
import { kebabCase } from '../utils/string'

export type DefaultsInstance =
	| undefined
	| {
			[key: string]: undefined | Record<string, unknown>
	  }

export const DefaultsSymbol: InjectionKey<Ref<DefaultsInstance>> =
	Symbol.for('ui-kit:defaults')

export function createDefaults(
	options?: DefaultsInstance
): Ref<DefaultsInstance> {
	return ref(options)
}

export function injectDefaults() {
	const defaults = inject(DefaultsSymbol)

	if (!defaults) {
		throw new Error('[UiKit] Could not find defaults instance')
	}

	return defaults
}

export function updateDefaults(key: string, value: Record<string, unknown>) {
	const defaults = injectDefaults()

	defaults.value = {
		...defaults.value,
		[key]: {
			...(defaults.value && defaults.value[key] ? defaults.value[key] : {}),
			...(value || {})
		}
	}
}

export function provideDefaults(
	defaults?: MaybeRef<DefaultsInstance | undefined>
) {
	const injectedDefaults = injectDefaults()
	const providedDefaults = ref(defaults)

	const newDefaults = computed(() => {
		if (providedDefaults.value == null) {
			return injectedDefaults.value
		}

		return mergeDeep(providedDefaults.value, {
			prev: injectedDefaults.value
		})
	}) as ComputedRef<DefaultsInstance>

	provide(DefaultsSymbol, newDefaults)

	return newDefaults
}

function propIsDefined(vnode: VNode, prop: string) {
	return (
		vnode.props &&
		(typeof vnode.props[prop] !== 'undefined' ||
			typeof vnode.props[kebabCase(prop)] !== 'undefined')
	)
}

export function useDefaults<T extends Record<string, unknown>>(
	_props: T,
	_name?: string
): T
export function useDefaults(
	_props?: undefined,
	_name?: string
): Record<string, unknown>
export function useDefaults(
	props: Record<string, unknown> = {},
	name?: string
) {
	const vm = getCurrentInstance('useDefaults')

	name = name ?? vm.type.name ?? vm.type.__name
	if (!name) {
		throw new Error('[UiKit] Could not determine component name')
	}

	const defaults = injectDefaults()

	const componentDefaults = computed(
		() => defaults.value?.[(props._as as string) ?? name]
	)
	const _props = new Proxy(props, {
		get(target, prop: string) {
			const propValue = Reflect.get(target, prop)

			if (prop === 'class' || prop === 'style') {
				return [componentDefaults.value?.[prop], propValue].filter(
					(v) => v != null
				)
			}

			if (propIsDefined(vm.vnode, prop)) {
				return propValue
			}

			const _componentDefault = componentDefaults.value?.[prop]
			if (_componentDefault !== undefined) {
				return _componentDefault
			}

			return propValue
		}
	})

	return _props
}
